import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import InspectionsTable from '../../components/InspectionsTable';
import PendingCards from '../../components/PendingCards';
import inspectionService from '../../services/inspectionService';
import './DashboardPage.css';
import { INSPECTION_STATUS, ROLES } from '../../utils/constants';
import { useAuthContext } from '../../context/AuthContext';
import { useSpinner } from '../../context/SpinnerContext';
import InspectionFormModal from '../../components/InspectionFormModal';
import logger from '../../utils/logger';

const { PENDING_USER, PENDING_REVIEW, APPROVED, REJECTED } = INSPECTION_STATUS;
const { INSPECTOR, CREATOR, CREATOR_SENIOR, ADMINISTRATOR } = ROLES;

const getAllowedStatus = (userRole) => {
    if (userRole === ADMINISTRATOR || userRole === CREATOR_SENIOR) {
        return [PENDING_USER, PENDING_REVIEW, APPROVED, REJECTED];
    }

    if (userRole === CREATOR || userRole === INSPECTOR) {
        return [PENDING_USER, PENDING_REVIEW];
    }

    return [PENDING_USER, PENDING_REVIEW];
};

const DashboardPage = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [inspectionData, setInspectionData] = useState([]);
    const [inspectionDataFiltered, setInspectionDataFiltered] = useState([]);
    const [pendingInspections, setPendingInspections] = useState([]);
    const [error, setError] = useState(null);

    const hasLoaded = useRef(false);
    const { currentUser } = useAuthContext();
    const { showSpinner, hideSpinner } = useSpinner();

    const [stats, setStats] = useState(() => {
        const allowedStatus = getAllowedStatus(currentUser?.role);
        const initialStats = {};
        allowedStatus.forEach(status => {
            initialStats[status] = 0;
        });
        return initialStats;
    });

    const getAllowedStatusForCurrentUser = () => {
        return getAllowedStatus(currentUser?.role);
    };
    
    const allStatus = getAllowedStatusForCurrentUser().join(',');

    const loadInspections = useCallback(async (status = activeTab) => {
        try {
            showSpinner('Cargando inspecciones...');
            logger.info('Cargando inspecciones...', {
                status: status,
                currentUser: currentUser,
            }, 'DashboardPage');

            setError(null);
            const promises = [];
            setActiveTab(undefined);

            const allowedStatus = getAllowedStatus(currentUser?.role);

            const statusToQuery = status || allowedStatus.join(',');

            promises.push(inspectionService.getInspections({
                status: statusToQuery,
                ...([INSPECTOR, CREATOR, CREATOR_SENIOR].includes(currentUser.role) ? { insuranceCompany: currentUser.insuranceCompany } : {})
            }));
            promises.push(inspectionService.getInspectionStats({
                ...([INSPECTOR, CREATOR, CREATOR_SENIOR].includes(currentUser.role) ? { insuranceCompany: currentUser.insuranceCompany } : {})
            }));
            promises.push(inspectionService.getPendingInspections({
                ...([INSPECTOR, CREATOR, CREATOR_SENIOR].includes(currentUser.role) ? { insuranceCompany: currentUser.insuranceCompany } : {})
            }));

            const results = await Promise.all(promises);
            const inspections = results.filter(result => result.inspections).flatMap(result => result.inspections);
            const statsResult = results.filter(result => result.stats).flatMap(result => result.stats)[0];
            const pendingInspections = results.filter(result => result.pendingInspections).flatMap(result => result.pendingInspections);

            const allowedStatusSet = new Set(allowedStatus);
            const filteredInspections = inspections.filter(inspection =>
                allowedStatusSet.has(inspection.status)
            );

            setInspectionData(filteredInspections);
            setInspectionDataFiltered(filteredInspections);

            const filteredStats = {};
            allowedStatus.forEach(status => {
                filteredStats[status] = statsResult?.[status] || 0;
            });
            setStats(filteredStats);

            setPendingInspections(pendingInspections);
        } catch (error) {
            setError(error.message);
            logger.error('Error cargando inspecciones...', error, {
                status: status,
                currentUser: currentUser,
            }, 'DashboardPage');
        } finally {
            hideSpinner();
            logger.info('Inspecciones cargadas exitosamente...', {
                status: status,
                currentUser: currentUser,
            }, 'DashboardPage');
        }
    }, [activeTab, currentUser, showSpinner, hideSpinner]);

    const handleTabChange = (newTab) => {
        setActiveTab(newTab);
        const inspectionsFiltered = inspectionData.filter(inspection => inspection.status === newTab);
        setInspectionDataFiltered(inspectionsFiltered);
    };

    const handleRefresh = useCallback(async () => {
        await loadInspections(allStatus);
    }, [loadInspections]);

    const validateActiveTab = (tabName) => {
        if (tabName === activeTab) {
            return true;
        }
        return false;
    }

    const handleCardDetail = (id) => {
        logger.info('Navegando a detalle de inspección...', {
            id: id,
        }, 'DashboardPage');
        navigate(`/inspeccion/${id}`);
    };

    const handleCardVerdict = (id) => {
        logger.info('Navegando a dictamen de inspección...', {
            id: id,
        }, 'DashboardPage');
        navigate(`/dictamenes/${id}`);
    };

    const handleInspectionCreated = useCallback(() => {
        setIsModalOpen(false);
        loadInspections(allStatus);
    }, [loadInspections]);

    useEffect(() => {
        if (hasLoaded.current) {
            return;
        }

        hasLoaded.current = true;
        loadInspections(allStatus);
    }, [loadInspections]);

    return (
        <div className="dashboard-page">
            <Header />

            <main className="dashboard-content">
                <div className="dashboard-container">
                    {/* Page Title */}
                    <div className="dashboard-header">
                        <h1 className="page-title">Panel de Inspecciones</h1>
                        <p className="page-subtitle">
                            Visualiza y gestiona las inspecciones pendientes, asignadas y en soporte de tu compañía.
                        </p>
                    </div>

                    {/* Pending Cards Carousel */}
                    <PendingCards
                        inspections={pendingInspections}
                        showImages={true}
                        navigateToDetail={handleCardDetail}
                        navigateToVerdict={handleCardVerdict}
                    />

                    {/* Navigation Tabs */}
                    <div className="content-tabs">
                        <button
                            className={`content-tab ${validateActiveTab(PENDING_USER) ? 'active' : ''} ${stats[PENDING_USER] < 1 ? 'disabled' : ''}`}
                            onClick={() => handleTabChange(PENDING_USER)}
                            disabled={validateActiveTab(PENDING_USER) || stats[PENDING_USER] < 1}
                        >
                            Pendientes por Usuario
                            <span className="tab-count">{stats[PENDING_USER]}</span>
                        </button>
                        <button
                            className={`content-tab ${validateActiveTab(PENDING_REVIEW) ? 'active' : ''} ${stats[PENDING_REVIEW] < 1 ? 'disabled' : ''}`}
                            onClick={() => handleTabChange(PENDING_REVIEW)}
                            disabled={validateActiveTab(PENDING_REVIEW) || stats[PENDING_REVIEW] < 1}
                        >
                            Pendientes por Revisión
                            <span className="tab-count">{stats[PENDING_REVIEW]}</span>
                        </button>

                        {(currentUser.role === ADMINISTRATOR || currentUser.role === CREATOR_SENIOR) && (
                            <>
                                <button
                                    className={`content-tab ${validateActiveTab(APPROVED) ? 'active' : ''} ${stats[APPROVED] < 1 ? 'disabled' : ''}`}
                                    onClick={() => handleTabChange(APPROVED)}
                                    disabled={validateActiveTab(APPROVED) || stats[APPROVED] < 1}
                                >
                                    Aprobadas
                                    <span className="tab-count">{stats[APPROVED]}</span>
                                </button>
                                <button
                                    className={`content-tab ${validateActiveTab(REJECTED) ? 'active' : ''} ${stats[REJECTED] < 1 ? 'disabled' : ''}`}
                                    onClick={() => handleTabChange(REJECTED)}
                                    disabled={validateActiveTab(REJECTED) || stats[REJECTED] < 1}
                                >
                                    Rechazadas
                                    <span className="tab-count">{stats[REJECTED]}</span>
                                </button>
                            </>
                        )}
                    </div>

                    {/* Main Content Table */}
                    <InspectionsTable
                        data={inspectionDataFiltered}
                        error={error}
                        onRefresh={handleRefresh}
                        onAddInspection={() => setIsModalOpen(true)}
                        navigateToDetail={(id) => navigate(`/${id.includes('DIC') ? 'dictamenes' : 'inspeccion'}/${id}`)}
                    />
                </div>
            </main>

            <Footer />

            <InspectionFormModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onInspectionCreated={handleInspectionCreated}
            />
        </div>
    );
};

export default DashboardPage;